import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileBarChart2, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [k, setK] = useState(null);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/dashboard/kpis").then(({ data }) => setK(data)); }, []);

  const genSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/generate-summary");
      setSummary(data.summary);
      toast.success("Executive summary generato");
    } catch (e) { toast.error("Errore AI"); } finally { setLoading(false); }
  };

  if (!k) return <div>Caricamento…</div>;

  const scoreColor = k.tcf_score >= 60 ? "#166534" : k.tcf_score >= 40 ? "#CA8A04" : "#991B1B";
  const scorePct = Math.max(0, Math.min(100, k.tcf_score)) / 100;
  const dash = 240 * scorePct;
  const breakdown = [
    { label: "Mappatura (0-30)", value: k.score_breakdown.map, max: 30 },
    { label: "Controlli (0-40)", value: k.score_breakdown.controls, max: 40 },
    { label: "Test L2 (0-30)", value: k.score_breakdown.tests, max: 30 },
  ];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="reports-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Reporting</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">TCF Score & Report</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={genSummary} disabled={loading} variant="outline" className="rounded-sm" data-testid="gen-summary-btn"><Sparkles size={14} className="mr-1" /> {loading ? "Generazione…" : "Executive Summary AI"}</Button>
          <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="export-excel-btn" onClick={() => toast.info("Export Excel ADE - feature roadmap v1.1")}>
            <Download size={14} className="mr-1" /> Export ADE Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-5 card-flat p-6">
          <div className="label-tag">TCF Maturity</div>
          <div className="h-60 mt-2 flex justify-center items-center">
            <svg width="280" height="160" viewBox="0 0 280 160">
              <path d="M 30 140 A 110 110 0 0 1 250 140" stroke="#F1F5F9" strokeWidth="18" fill="none" strokeLinecap="round" />
              <path d="M 30 140 A 110 110 0 0 1 250 140" stroke={scoreColor} strokeWidth="18" fill="none" strokeLinecap="round" strokeDasharray={`${345 * scorePct} 400`} />
            </svg>
          </div>
          <div className="text-center -mt-12">
            <div className="data-num text-5xl font-bold text-slate-900">{k.tcf_score}</div>
            <div className="text-xs text-slate-500">/100 · benchmark {k.industry_benchmark}</div>
          </div>
        </Card>

        <Card className="col-span-7 card-flat p-6">
          <div className="label-tag">Composizione Score</div>
          <div className="space-y-4 mt-4">
            {breakdown.map((b, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-700">{b.label}</span>
                  <span className="data-num text-slate-900 font-semibold">{b.value} / {b.max}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
                  <div className="h-full bg-slate-900" style={{ width: `${(b.value / b.max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="card-flat p-6" data-testid="benchmark-card">
        <div className="label-tag">Benchmark di settore</div>
        <p className="text-sm text-slate-700 mt-2">
          Il tuo TCF Score di <span className="data-num font-semibold">{k.tcf_score}</span> si confronta con la media del settore (<span className="data-num font-semibold">{k.industry_benchmark}</span>).
          {k.tcf_score >= k.industry_benchmark ? " Sei sopra la media del settore — ottimo lavoro." : " Sei sotto la media: prioritizza la valutazione dei rischi e i test L2."}
        </p>
      </Card>

      {summary && (
        <Card className="card-flat p-6" data-testid="summary-card">
          <div className="label-tag mb-3">Executive Summary</div>
          <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{summary}</div>
        </Card>
      )}
    </div>
  );
}
